import { type LucideIcon } from "lucide-react";
import { type ReactNode } from "react";

import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

interface MetricCardProps {
  label: string;
  icon: LucideIcon;
  isLoading: boolean;
  isError?: boolean;
  className?: string;
  children: ReactNode;
}

/**
 * Shared shell for every dashboard metric tile. Provides the
 * eyebrow label, the right-aligned icon, the loading skeleton, and
 * a soft border that lifts on hover. Individual tiles render their
 * own ``children`` (a big number, a list, etc.).
 *
 * Aesthetic rules baked in here:
 *   - Eyebrow label uses ``label-eyebrow`` utility (ocean-600,
 *     uppercase tracking-wide). This is the ONLY ALL-CAPS surface
 *     allowed in the product.
 *   - Border defaults to ``ocean-100`` and hovers to
 *     ``ocean-200``. No shadows, no gradients.
 */
export function MetricCard({
  label,
  icon: Icon,
  isLoading,
  isError = false,
  className,
  children,
}: MetricCardProps) {
  return (
    <div
      className={cn(
        "rounded-lg border border-ocean-100 bg-white p-5 transition-colors hover:border-ocean-200",
        isError && "border-warning-600/30 bg-warning-50/30",
        className
      )}
    >
      <div className="mb-3 flex items-center justify-between">
        <span className="label-eyebrow">{label}</span>
        <Icon size={16} className="text-ocean-400" />
      </div>
      {isLoading ? <Skeleton className="h-8 w-24" /> : children}
    </div>
  );
}
